import { Chapter } from '../types';
import { ChapterService } from '../services/chapter.service';

/**
 * Loads chapter content using the ChapterNoteService
 * @param chapterId The ID of the chapter to load
 * @param ChapterNoteService The service to use for loading chapter data
 * @param setLoading State setter for loading status
 * @param setContentData State setter for content data
 * @param setEditableChapterName State setter for editable chapter name
 * @param setIsNewContent State setter for new content flag
 * @param setShowContent State setter for show content flag
 */
export const loadChapterContent = async (
  chapterId: number,
  ChapterNoteService: any,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setContentData: React.Dispatch<React.SetStateAction<any>>,
  setEditableChapterName: React.Dispatch<React.SetStateAction<string>>,
  setIsNewContent: React.Dispatch<React.SetStateAction<boolean>>,
  setShowContent: React.Dispatch<React.SetStateAction<boolean>>
): Promise<void> => {
  try {
    setLoading(true);
    
    // Use the new API endpoint to get all chapter data in a single request
    const chapterData = await ChapterNoteService.getChapterNotes(chapterId);
    
    if (!chapterData) {
      throw new Error('Chapter data not found');
    }
    
    // Set content data - the API already provides the complete structure we need
    setContentData({
      chapter: chapterData.chapter,
      notes: chapterData.notes
    });
    
    setEditableChapterName(chapterData.chapter);
    setIsNewContent(false);
    setShowContent(true);
    
  } catch (err) {
    console.error('Error loading chapter content:', err);
    alert('Failed to load chapter content. Please try again later.');
  } finally {
    setLoading(false);
  }
};

/**
 * Handles saving a new chapter
 * @param newChapterName The name of the new chapter
 * @param subjectId The ID of the subject to which the chapter belongs
 * @param chapters The current list of chapters
 * @param setChapters State setter for chapters
 * @param setShowAddForm State setter for showing add form
 * @param setNewChapterName State setter for new chapter name
 * @param setSelectedChapterId State setter for selected chapter ID
 * @param setContentData State setter for content data
 * @param setEditableChapterName State setter for editable chapter name
 * @param setIsNewContent State setter for new content flag
 * @param setShowContent State setter for show content flag
 * @param setIsSubmitting State setter for submission status
 * @param onSelectChapter Callback for chapter selection
 */
export const saveNewChapter = async (
  newChapterName: string,
  subjectId: number,
  chapters: Chapter[],
  setChapters: React.Dispatch<React.SetStateAction<Chapter[]>>,
  setShowAddForm: React.Dispatch<React.SetStateAction<boolean>>,
  setNewChapterName: React.Dispatch<React.SetStateAction<string>>,
  setSelectedChapterId: React.Dispatch<React.SetStateAction<string>>,
  setContentData: React.Dispatch<React.SetStateAction<any>>,
  setEditableChapterName: React.Dispatch<React.SetStateAction<string>>,
  setIsNewContent: React.Dispatch<React.SetStateAction<boolean>>,
  setShowContent: React.Dispatch<React.SetStateAction<boolean>>,
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
  onSelectChapter: (chapterId: number) => void
): Promise<void> => {
  if (!newChapterName.trim()) {
    alert('Please enter a chapter name');
    return;
  }
  
  if (!subjectId) {
    alert('No subject selected');
    return;
  }
  
  try {
    setIsSubmitting(true);
    
    const newChapter = await ChapterService.createChapter({
      name: newChapterName.trim(),
      subject_id: subjectId
    });
    
    // Update local state
    setChapters([...chapters, newChapter]);
    
    // Clear form and hide it
    setShowAddForm(false);
    setNewChapterName('');
    
    // Select the new chapter
    setSelectedChapterId(String(newChapter.id));
    
    // We'll handle displaying the content ourselves
    onSelectChapter(0);
    
    // Load the empty chapter content
    setContentData({
      chapter: newChapter.name,
      notes: []
    });
    setEditableChapterName(newChapter.name);
    setIsNewContent(false);
    setShowContent(true);
    
    // Show success message
    alert(`Chapter "${newChapter.name}" was successfully created!`);
  } catch (err) {
    console.error('Error creating chapter:', err);
    alert('Failed to create chapter. Please try again later.');
  } finally {
    setIsSubmitting(false);
  }
};

/**
 * Handles updating an existing chapter
 * @param editChapterName The new name for the chapter
 * @param editChapterId The ID of the chapter to update
 * @param chapters The current list of chapters
 * @param selectedChapterId The currently selected chapter ID
 * @param contentData The current content data
 * @param setChapters State setter for chapters
 * @param setShowEditForm State setter for showing edit form
 * @param setEditChapterId State setter for edit chapter ID
 * @param setEditChapterName State setter for edit chapter name
 * @param setContentData State setter for content data
 * @param setEditableChapterName State setter for editable chapter name
 * @param setIsSubmitting State setter for submission status
 */
export const updateExistingChapter = async (
  editChapterName: string,
  editChapterId: number,
  chapters: Chapter[],
  selectedChapterId: string,
  contentData: any,
  setChapters: React.Dispatch<React.SetStateAction<Chapter[]>>,
  setShowEditForm: React.Dispatch<React.SetStateAction<boolean>>,
  setEditChapterId: React.Dispatch<React.SetStateAction<number | null>>,
  setEditChapterName: React.Dispatch<React.SetStateAction<string>>,
  setContentData: React.Dispatch<React.SetStateAction<any>>,
  setEditableChapterName: React.Dispatch<React.SetStateAction<string>>,
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>
): Promise<void> => {
  if (!editChapterName.trim()) {
    alert('Please enter a chapter name');
    return;
  }
  
  if (!editChapterId) {
    alert('No chapter selected for editing');
    return;
  }
  
  try {
    setIsSubmitting(true);
    
    const updatedChapter = await ChapterService.updateChapter(editChapterId, {
      name: editChapterName.trim()
    });
    
    // Update local state
    setChapters(chapters.map(chapter => 
      chapter.id === editChapterId ? updatedChapter : chapter
    ));
    
    // Update content data if this is the selected chapter
    if (selectedChapterId === String(editChapterId) && contentData) {
      setContentData({
        ...contentData,
        chapter: updatedChapter.name
      });
      setEditableChapterName(updatedChapter.name);
    }
    
    // Clear form and hide it
    setShowEditForm(false);
    setEditChapterId(null);
    setEditChapterName('');
    
    // Show success message
    alert(`Chapter "${updatedChapter.name}" was successfully updated!`);
  } catch (err) {
    console.error('Error updating chapter:', err);
    alert('Failed to update chapter. Please try again later.');
  } finally {
    setIsSubmitting(false);
  }
};

/**
 * Handles deleting a chapter
 * @param selectedChapterId The ID of the chapter to delete
 * @param chapters The current list of chapters
 * @param setChapters State setter for chapters
 * @param setSelectedChapterId State setter for selected chapter ID
 * @param setShowContent State setter for show content flag
 * @param setContentData State setter for content data
 * @param setIsSubmitting State setter for submission status
 * @param onSelectChapter Callback for chapter selection
 */
export const deleteChapter = async (
  selectedChapterId: string,
  chapters: Chapter[],
  setChapters: React.Dispatch<React.SetStateAction<Chapter[]>>,
  setSelectedChapterId: React.Dispatch<React.SetStateAction<string>>,
  setShowContent: React.Dispatch<React.SetStateAction<boolean>>,
  setContentData: React.Dispatch<React.SetStateAction<any>>,
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
  onSelectChapter: (chapterId: number) => void
): Promise<void> => {
  if (!selectedChapterId) {
    alert('Please select a chapter to delete');
    return;
  }

  const chapterId = parseInt(selectedChapterId, 10);
  const chapterToDelete = chapters.find(c => c.id === chapterId);
  
  if (!chapterToDelete) {
    alert('Chapter not found');
    return;
  }
  
  if (window.confirm(`Are you sure you want to delete the chapter "${chapterToDelete.name}"? This action cannot be undone.`)) {
    try {
      setIsSubmitting(true);
      await ChapterService.deleteChapter(chapterId);
      
      // Update local state
      setChapters(chapters.filter(chapter => chapter.id !== chapterId));
      setSelectedChapterId('');
      onSelectChapter(0);
      setShowContent(false);
      setContentData(null);
      
      // Show success message
      alert(`Chapter "${chapterToDelete.name}" was successfully deleted!`);
    } catch (err) {
      console.error('Error deleting chapter:', err);
      alert('Failed to delete chapter. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  }
};